import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  isLoggedIn,
  clearAuthData,
  saveAuthData,
  saveUserId,
  getUserId,
  isRefreshTokenExpired,
  saveIsTainted,
  getIsTainted,
  clearIsTainted,
} from '../utils/auth';
import { getApiVersion, getMembershipMe, getPointsBalance } from '../utils/api';
import { refreshAccessToken, isAccessTokenExpiringSoon } from '../utils/api';

const AuthContext = createContext(null);

/**
 * 认证上下文 Provider
 */
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiVersion, setApiVersion] = useState('');
  const [isSilentRefreshing, setIsSilentRefreshing] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isTainted, setIsTainted] = useState(false);
  const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [points, setPoints] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);

  // 初始化：检查登录状态
  useEffect(() => {
    const checkAuth = () => {
      const loggedIn = isLoggedIn();
      setIsAuthenticated(loggedIn);

      if (loggedIn) {
        const id = getUserId();
        setUserId(id);

        // 检查 tainted 状态
        const tainted = getIsTainted();
        setIsTainted(tainted);
        if (tainted) {
          setShowInviteCodeModal(true);
        }

        // 如果 token 即将过期，进行静默刷新
        if (isAccessTokenExpiringSoon()) {
          silentRefreshToken();
        }

        refreshMembership();
        refreshPoints();
      } else {
        // 如果用户之前已登录但现在未登录，且refresh token过期，显示登录模态框
        const previouslyLoggedIn = localStorage.getItem('was_logged_in');
        if (previouslyLoggedIn && isRefreshTokenExpired()) {
          setShowLoginModal(true);
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  // 监听全局登出事件
  useEffect(() => {
    const handleLogout = () => {
      logout();
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // 静默刷新 token
  const silentRefreshToken = useCallback(async () => {
    if (isSilentRefreshing) return;

    try {
      setIsSilentRefreshing(true);
      const success = await refreshAccessToken();

      if (!success) {
        // 刷新失败，触发登出
        logout();
      }
    } catch (error) {
      console.error('静默刷新 token 失败:', error);
    } finally {
      setIsSilentRefreshing(false);
    }
  }, [isSilentRefreshing]);

  const refreshMembership = useCallback(async () => {
    if (membershipLoading) return null;
    try {
      setMembershipLoading(true);
      const res = await getMembershipMe();
      if (res?.code === 0) {
        setMembership(res.data || null);
        return res.data || null;
      }
      setMembership(null);
      return null;
    } catch (error) {
      setMembership(null);
      return null;
    } finally {
      setMembershipLoading(false);
    }
  }, [membershipLoading]);

  const refreshPoints = useCallback(async () => {
    if (pointsLoading) return null;
    try {
      setPointsLoading(true);
      const res = await getPointsBalance();
      if (res?.code === 0) {
        setPoints(res.data || null);
        return res.data || null;
      }
      setPoints(null);
      return null;
    } catch (error) {
      setPoints(null);
      return null;
    } finally {
      setPointsLoading(false);
    }
  }, [pointsLoading]);

  // 登录
  const login = useCallback((authData) => {
    if (!authData || !authData.data) {
      console.error('登录数据格式错误');
      return;
    }

    saveAuthData(authData.data);
    setIsAuthenticated(true);

    // 记录用户已登录的状态
    localStorage.setItem('was_logged_in', 'true');

    // 如果有 user_id，保存
    if (authData.user_id) {
      saveUserId(authData.user_id);
      setUserId(authData.user_id);
    }

    // 保存 tainted 状态
    if (authData.data.is_tainted !== undefined) {
      saveIsTainted(authData.data.is_tainted);
      setIsTainted(authData.data.is_tainted);
      if (authData.data.is_tainted) {
        setShowInviteCodeModal(true);
      }
    }

    // 关闭登录模态框
    setShowLoginModal(false);

    // 登录后刷新会员信息
    refreshMembership();
    refreshPoints();
  }, [refreshMembership, refreshPoints]);

  // 登出
  const logout = useCallback(() => {
    clearAuthData();
    clearIsTainted();
    setIsAuthenticated(false);
    setUserId(null);
    setIsSilentRefreshing(false);
    setShowLoginModal(false);
    setIsTainted(false);
    setShowInviteCodeModal(false);
    setMembership(null);
    setMembershipLoading(false);
    setPoints(null);
    setPointsLoading(false);
    localStorage.removeItem('was_logged_in');
  }, []);

  // 清除污点状态
  const clearTainted = useCallback(() => {
    saveIsTainted(false);
    setIsTainted(false);
    setShowInviteCodeModal(false);
  }, []);

  
  const value = {
    isAuthenticated,
    userId,
    loading,
    apiVersion,
    isSilentRefreshing,
    showLoginModal,
    setShowLoginModal,
    isTainted,
    showInviteCodeModal,
    setShowInviteCodeModal,
    membership,
    membershipLoading,
    refreshMembership,
    points,
    pointsLoading,
    refreshPoints,
    login,
    logout,
    clearTainted,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 使用认证上下文
 * @returns {object} 认证上下文
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return context;
}
